import { AfterViewInit, ChangeDetectionStrategy, Component, computed, forwardRef, inject, Injector, input, model, OnInit, signal } from "@angular/core";
import { ControlValueAccessor, FormControl, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors } from "@angular/forms";
import { BiTranslateDirective, BiTranslatePipe, BiTranslateService } from "@globals/bi-translate";
import { CustomValidators } from "@globals/classes/CustomValidators";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { isStringNullOrEmpty, uniqueID } from "@globals/helper-functions";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { InputTextModule } from "primeng/inputtext";
import { take } from "rxjs";

/**
 * Custom input for entering a zip code. Validation logic and error message displaying is included!
 **/
@Component({
  selector: "bi-zip-code-input",
  imports: [InputTextModule, FormsModule, BiTranslatePipe, BiTranslateDirective, InputGroupAddonModule, InputGroupModule],
  templateUrl: "./bi-zip-code-input.component.html",
  styles: [":host{display: block}"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BiZipCodeInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BiZipCodeInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export default class BiZipCodeInputComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  /**
   * Label value that will be displayed inside the grey box being added in front of input.
   * If this is false or undfined, the box will not be shown - e.i. normal input style.
   * Default undefined.
   */
  public readonly inputGroupLabel = input<string>();

  /**
   * Label for the input. Default is translated value of "zipcode"
   */
  public readonly label = model<string>();

  /**
   * Whether the component will show a label for the input. Default is true
   */
  public readonly hasLabel = input(true);

  public readonly countryId = input<BiCountryId>();

  public readonly formControl = model<FormControl>();

  protected textValue = signal<string>("");

  protected disabled = signal(false);

  public readonly uniqueInputId = signal(uniqueID()); // for setting a unique html id on the input

  /**
   * Max. length allowed for input. As the zip code's length can defer in length depending on country
   */
  protected readonly inputMaxLength = computed(() => {
    return this.countryId() !== BiCountryId.FI && this.countryId() !== BiCountryId.SE ? 4 : 5;
  });

  private propagateChange: (val: string) => void;
  private onTouched: () => void;

  private readonly injector = inject(Injector);
  private readonly translator = inject(BiTranslateService);

  public ngOnInit() {
    if (!this.label()) {
      this.translator
        .get("shared.Zipcode")
        .pipe(take(1))
        .subscribe(zipcodeText => this.label.set(zipcodeText));
    }
  }

  public ngAfterViewInit() {
    // If client didn't use a [FormControl] binding, then we have to inject the ref. to it ourselves
    if (!this.formControl()) this.formControl.set(this.injector.get(NgControl)?.control as FormControl);
  }

  public writeValue(val: string): void {
    // By using only double equal sign (==), we test against undefined and null
    if (val == this.textValue()) return;

    this.textValue.set(val);
    if (this.propagateChange) this.propagateChange(val);
  }

  public registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean) {
    this.disabled.set(isDisabled);
  }

  public onChanged(event: KeyboardEvent) {
    if (event.key === "Tab") return;

    this.propagateChange((event.target as HTMLInputElement).value);
    if (this.onTouched) this.onTouched();
  }

  /**
   * Customer valdiation being run on the control so that clients don't
   * have to manually add validators.
   */
  public validate(control: FormControl): ValidationErrors | null {
    if (isStringNullOrEmpty(control.value)) return { required: true };

    // In Sweden and Denmark, the zipcode MUST have an exact length
    const lengthErr = CustomValidators.checkLength(this.inputMaxLength())(control);
    if (lengthErr != null) return lengthErr;

    if (control.value.length > this.inputMaxLength()) return { maxLength: true };

    // Last validation so just return it's result
    return CustomValidators.digitsOnly(false)(control);
  }
}
